import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { formatMoney } from '@shared/lib/format';
import { toast } from '@shared/ui/feedback/Toast';
import { fetchCart, updateCartItem, removeCartItem } from './api';

export function Cart(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const cartQuery = useQuery({ queryKey: ['cart'], queryFn: fetchCart });
  const inApp = location.pathname.startsWith('/app/');
  const productsRoute = inApp ? '/app/products' : '/products/search';
  const checkoutRoute = inApp ? '/app/checkout' : '/checkout';

  const updateMutation = useMutation({
    mutationFn: ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => updateCartItem(cartItemId, quantity),
    onSuccess: (cart) => queryClient.setQueryData(['cart'], cart),
  });

  const removeMutation = useMutation({
    mutationFn: removeCartItem,
    onSuccess: (cart) => {
      queryClient.setQueryData(['cart'], cart);
      toast.success('Item removed');
    },
  });

  if (cartQuery.isLoading) return <Page><div className="flex flex-col gap-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div></Page>;
  if (cartQuery.isError) return <Page><ErrorState onRetry={() => cartQuery.refetch()} /></Page>;
  const cart = cartQuery.data;
  if (!cart || cart.items.length === 0) {
    return (
      <Page>
        <EmptyState
          icon={<ShoppingCart className="h-7 w-7" strokeWidth={1.5} />}
          title="Cart's empty."
          description="Browse the store to add items."
          action={<Button onClick={() => navigate(productsRoute)}>Browse products</Button>}
        />
      </Page>
    );
  }

  return (
    <Page>
      <div className="flex flex-col gap-3">
        {cart.items.map((item) => (
          <Card key={item.cart_item_id} noGrain className="p-3">
            <div className="flex gap-3 items-center">
              <div className="h-16 w-16 rounded-md overflow-hidden bg-sunken hairline flex-shrink-0">
                {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-body font-semibold text-ink truncate">{item.name}</p>
                <p className="text-caption text-ink-muted">{formatMoney(item.unit_price, { currency: item.currency })} each</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Decrease"
                  onClick={() => updateMutation.mutate({ cartItemId: item.cart_item_id, quantity: Math.max(0, item.quantity - 1) })}
                  className="flex h-8 w-8 items-center justify-center rounded-pill bg-sunken text-ink-muted hover:bg-hairline transition-colors"
                >
                  <Minus className="h-3 w-3" strokeWidth={1.75} />
                </button>
                <span className="font-display text-body w-6 text-center tabular">{item.quantity}</span>
                <button
                  type="button"
                  aria-label="Increase"
                  onClick={() => updateMutation.mutate({ cartItemId: item.cart_item_id, quantity: item.quantity + 1 })}
                  className="flex h-8 w-8 items-center justify-center rounded-pill bg-sunken text-ink-muted hover:bg-hairline transition-colors"
                >
                  <Plus className="h-3 w-3" strokeWidth={1.75} />
                </button>
              </div>
            </div>
            <div className="flex justify-between items-center mt-2 pl-[76px]">
              <button
                type="button"
                onClick={() => removeMutation.mutate(item.cart_item_id)}
                className="text-caption text-danger flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                Remove
              </button>
              <span className="text-body font-display tabular text-ink">{formatMoney(item.line_total, { currency: item.currency })}</span>
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-6">
        <Row label="Subtotal" value={formatMoney(cart.subtotal, { currency: cart.currency })} />
        {cart.shipping !== undefined ? <Row label="Shipping" value={formatMoney(cart.shipping, { currency: cart.currency })} /> : null}
        {cart.tax !== undefined ? <Row label="Tax" value={formatMoney(cart.tax, { currency: cart.currency })} /> : null}
        <div className="hairline-t mt-2 pt-2">
          <Row label="Total" value={formatMoney(cart.total, { currency: cart.currency })} bold />
        </div>
      </Card>

      <ActionButton className="mt-6" onClick={() => navigate(checkoutRoute)}>
        Checkout
      </ActionButton>
    </Page>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between items-baseline py-1">
      <span className={bold ? 'text-body font-semibold text-ink' : 'text-body-sm text-ink-secondary'}>{label}</span>
      <span className={`tabular ${bold ? 'font-display text-headline text-ink' : 'text-body text-ink'}`}>{value}</span>
    </div>
  );
}
